<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import ReleaseControls from '@voix/components/chrome/raft/ReleaseControls.vue'
import { useAdminStore } from '@voix/store/adminStore'
import { useRoute } from 'vue-router'

// Messages
import { useCookie } from '#imports'

const adminStore = useAdminStore()

// Releases
const releasePreviewCookie = useCookie('voix-release-preview')
const isViewingRelease = computed(() => releasePreviewCookie.value)

// Controls

const showRaft = ref(false)

// Should the raft even be visible?
const temporaryShowRaft = computed(() => {
  return isViewingRelease.value
})

// Which tab should be active?
const activeTab = ref('releases')

function setActiveTab(tab: string) {
  activeTab.value = tab
}

function checkForSomethingNewToShow() {
  // set the active tab to releases if there is a release preview
  if (isViewingRelease.value)
    activeTab.value = 'releases'
}

checkForSomethingNewToShow()

watch(temporaryShowRaft, () => {
  checkForSomethingNewToShow()
})

const route = useRoute()
const isVoixStudio = computed(() => {
  return route.name === 'voix-studio-en'
})

function zoomIn() {
  adminStore.currentScale += 0.1
  if (adminStore.currentScale > 1)
    adminStore.currentScale = 1
}

function zoomOut() {
  adminStore.currentScale -= 0.1
  if (adminStore.currentScale < 0.2)
    adminStore.currentScale = 0.2
}

function resetZoom() {
  adminStore.currentScale = 1
}
</script>

<template>
  <div
    class="pointer-events-none fixed z-10 pl-[280px] bottom-5 right-2 flex justify-center text-gray-100"
  >
    <div
      class="flex flex-col items-center justify-stretch w-full transition"
    >
      <div
        class="pointer-events-auto flex text-sm font-medium"
      >
        <div
          class="grid grid-cols-4 gap-1.5 text-sm pointer-events-auto"
        >
          <div class="relative">
            <button
              v-voix-popper:#app.top="`<span class='text-xs'>Zen Mode</span>`"
              class="p-2 voix-admin-text voix-admin-border-light border rounded"
              :class="{
                'voix-admin-bg-light': adminStore.isZenMode,
              }"
              @click="adminStore.isZenMode = !adminStore.isZenMode"
            >
              <svg
                v-if="adminStore.isZenMode"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <svg
                v-if="!adminStore.isZenMode"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                />
              </svg>
            </button>
          </div>
          <div class="relative">
            <button
              v-voix-popper:#app.top="`<span class='text-xs'>Zoom In</span>`"
              class="p-2 voix-admin-text voix-admin-border-light border rounded"
              @click="zoomIn"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
                />
              </svg>
            </button>
          </div>
          <div class="relative">
            <button
              v-voix-popper:#app.top="`<span class='text-xs'>Zoom Out</span>`"
              class="p-2 voix-admin-text voix-admin-border-light border rounded"
              @click="zoomOut"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6"
                />
              </svg>
            </button>
          </div>
          <div class="relative">
            <button
              v-voix-popper:#app.top="`<span class='text-xs'>Reset Zoom</span>`"
              class="p-2 voix-admin-text voix-admin-border-light border rounded"
              @click="resetZoom"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-4 h-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
